import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import CaseStudy from "./CaseStudy";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "../data/content";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [activeStudy, setActiveStudy] = useState(null);
  const items = PORTFOLIO.filter((p) => filter === "All" || p.category === filter);

  return (
    <section id="work" className="py-20 sm:py-28 lg:py-32 bg-white scroll-mt-16" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <SectionHeading
          eyebrow="Selected Work"
          title="Built inside the world's sharpest design rooms"
          subtitle="A decade of product development at Zara and H&M, design leadership at ITC, and a creative directorship that helped raise ₹50 Cr for FableStreet."
        />

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2">
          {PORTFOLIO_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`portfolio-filter-${f.replace(/[^a-z]/gi, "").toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                filter === f
                  ? "bg-[#1d1d1f] text-white border-[#1d1d1f]"
                  : "bg-white text-[#1d1d1f]/70 border-[#d2d2d7] hover:border-[#1d1d1f]"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((p) => (
              <motion.article
                layout
                key={p.brand + p.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                onClick={() => setActiveStudy(p)}
                className="group relative rounded-3xl overflow-hidden bg-[#f5f5f7] aspect-[4/5] cursor-pointer"
                data-testid={`portfolio-card-${p.brand.replace(/[^a-z]/gi, "").toLowerCase()}`}
              >
                <img
                  src={p.img}
                  alt={`${p.brand} — ${p.title}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7 text-white">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">{p.brand}</p>
                  <h3 className="mt-1.5 text-xl font-semibold tracking-tight flex items-center gap-2">
                    {p.title}
                    <ArrowUpRight size={16} className="opacity-70 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                  </h3>
                  <p className="mt-2 text-sm text-white/75 leading-relaxed">{p.desc}</p>
                  <p className="mt-3 flex items-center gap-1.5 text-[13px] font-medium text-white/90">
                    <BookOpen size={14} /> Read the case study
                  </p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {activeStudy && <CaseStudy study={activeStudy} onClose={() => setActiveStudy(null)} />}
      </AnimatePresence>
    </section>
  );
}
